/**
 * Home Page - Public landing page for Jonkai Studio
 * Showcases features and encourages sign-up
 */

import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../components/icons';

interface Feature {
  icon: IconName;
  title: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    icon: 'sparkles',
    title: 'AI Content Generation',
    description: 'Generate engaging social media content with AI that matches your brand voice.',
  },
  {
    icon: 'layers',
    title: 'Brand DNA Analysis',
    description: 'Analyze your website to extract your unique brand voice and personality.',
  },
  {
    icon: 'calendar',
    title: 'Smart Scheduling',
    description: 'Schedule posts across multiple platforms with our visual calendar.',
  },
  {
    icon: 'bar-chart',
    title: 'Analytics Dashboard',
    description: 'Track performance metrics and optimize your content strategy.',
  },
  {
    icon: 'megaphone',
    title: 'Ad Creative Builder',
    description: 'Design stunning ads with AI-powered copy for any platform.',
  },
  {
    icon: 'mail',
    title: 'Email Campaigns',
    description: 'Create automated email sequences that convert.',
  },
  {
    icon: 'globe',
    title: 'Landing Pages',
    description: 'Build conversion-optimized landing pages in minutes.',
  },
  {
    icon: 'zap',
    title: 'Bulk Generation',
    description: 'Generate weeks of content in one click with AI bulk creation.',
  },
];

const BENEFITS = [
  'AI-powered content that sounds like you',
  'Save 10+ hours per week on content creation',
  'Consistent brand voice across all platforms',
  'Data-driven insights to grow your audience',
  'All-in-one marketing platform',
];

export function Home() {
  return (
    <div className="min-h-screen bg-white">
      {/* Coming Soon Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-3 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 text-sm sm:text-base">
          <Icon name="alert" size="md" />
          <span className="font-medium">Coming Soon!</span>
          <span className="hidden sm:inline">Jonkai Studio is currently under development. Stay tuned for launch!</span>
          <span className="sm:hidden">Under development</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img src="/images/logo_only_k.png" alt="Jonkai Studio" className="w-8 h-8" />
              <span className="text-xl font-bold text-gray-900">Jonkai Studio</span>
            </div>
            <div className="flex items-center gap-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link
                to="/login"
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <img
              src="/images/logo_with_k.png"
              alt="Jonkai Studio"
              className="w-64 sm:w-80 mx-auto mb-10 object-contain"
            />
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="sparkles" size="sm" />
              AI-Powered Content Creation
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Create Content That
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Sounds Like You</span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Jonkai Studio uses AI to learn your brand voice and generate social media content,
              ads, emails, and landing pages that truly represent your brand.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/login"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all hover:shadow-lg hover:shadow-blue-200"
              >
                Start Creating Free
                <Icon name="arrow-right" size="md" />
              </Link>
              <a
                href="#features"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-gray-700 px-8 py-4 rounded-xl font-semibold text-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                See Features
              </a>
            </div>
          </div>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" />
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {BENEFITS.map((benefit, index) => (
              <div key={index} className="flex items-center gap-2 text-gray-700">
                <Icon name="check" size="md" className="flex-shrink-0" />
                <span className="font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everything You Need to Grow
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              From content creation to analytics, Jonkai Studio has all the tools you need
              to build a powerful online presence.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {FEATURES.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all group"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                  <Icon name={feature.icon} size="lg" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold">How It Works</h2>
            <p className="mt-4 text-lg text-blue-100">Get started in three simple steps</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Connect Your Brand',
                description: 'Enter your website URL and let our AI analyze your brand voice, tone, and style.',
              },
              {
                step: '02',
                title: 'Generate Content',
                description: 'Create posts, ads, emails, and landing pages with one click using your brand DNA.',
              },
              {
                step: '03',
                title: 'Publish & Grow',
                description: 'Schedule content, track performance, and watch your audience grow.',
              },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 text-2xl font-bold mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                <p className="text-blue-100">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 sm:py-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
            Ready to Transform Your Content?
          </h2>
          <p className="mt-4 text-lg text-gray-600">
            Join thousands of creators and businesses who use Jonkai Studio to create
            consistent, engaging content that grows their audience.
          </p>
          <div className="mt-10">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-blue-700 transition-all hover:shadow-lg hover:shadow-blue-200"
            >
              Get Started Free
              <Icon name="arrow-right" size="md" />
            </Link>
            <p className="mt-4 text-sm text-gray-500">No credit card required</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/images/logo_only_k.png" alt="Jonkai Studio" className="w-6 h-6" />
              <span className="text-lg font-bold text-white">Jonkai Studio</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
            </div>
            <p className="text-sm">
              © {new Date().getFullYear()} Jonkai Ventures. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
